import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface TableColumn<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  isLoading?: boolean;
  emptyMessage?: string;
  pageSize?: number;
  onRowClick?: (item: T) => void;
  keyExtractor: (item: T) => string | number;
}

export function Table<T>({
  data,
  columns,
  isLoading = false,
  emptyMessage = 'No data available',
  pageSize = 10,
  onRowClick,
  keyExtractor,
}: TableProps<T>) {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [localPageSize, setLocalPageSize] = useState(pageSize);

  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  // Reset to page 1 when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [safeData.length]);

  const handleSort = (key: string) => {
    const column = columns.find((col) => col.key === key);
    if (!column?.sortable) return;

    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.direction === 'asc' ? { key, direction: 'desc' } : null;
      }
      return { key, direction: 'asc' };
    });
  };

  // Sort before pagination for better performance
  const sortedData = useMemo(() => {
    if (!sortConfig) return safeData;

    return [...safeData].sort((a, b) => {
      const aValue = (a as any)[sortConfig.key];
      const bValue = (b as any)[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [safeData, sortConfig]);

  // Paginate after sorting
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * localPageSize;
    const endIndex = startIndex + localPageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, localPageSize]);

  if (isLoading) {
    return (
      <motion.div
        className="d-flex justify-content-center align-items-center py-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
      </motion.div>
    );
  }

  if (safeData.length === 0) {
    return (
      <motion.div
        className="text-center py-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5">
            <p className="text-muted mb-0" style={{ fontSize: '1.1rem' }}>{emptyMessage}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="table-modern-container"
      dir={isRTL ? 'rtl' : 'ltr'}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Table */}
      <div className="table-responsive">
        <table className="table table-modern">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    cursor: column.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort(column.key)}
                  className={column.sortable ? 'sortable' : ''}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>{column.header}</span>
                    {column.sortable && sortConfig?.key === column.key && (
                      <motion.span
                        initial={{ opacity: 0, rotate: -90 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        className="text-primary"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </motion.span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item) => (
              <motion.tr
                key={keyExtractor(item)}
                onClick={() => onRowClick?.(item)}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
                className="table-row-modern"
              >
                {columns.map((column) => (
                  <td key={column.key}>{column.render(item)}</td>
                ))}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(() => {
        const totalPages = Math.ceil(safeData.length / localPageSize);
        const startIndex = (currentPage - 1) * localPageSize;
        const endIndex = startIndex + localPageSize;
        
        if (totalPages <= 1) return null;
        
        return (
          <motion.div
            className="table-pagination"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="table-pagination-info">
              {t('table.showing', 'Showing')} <strong>{startIndex + 1}</strong> - <strong>{Math.min(endIndex, safeData.length)}</strong> {t('table.of', 'of')} <strong>{safeData.length}</strong>
            </div>

            <div className="table-pagination-controls">
              <motion.button
                className="table-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title={t('table.previous', 'Previous')}
                whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
                whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
              >
                {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </motion.button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="table-pagination-ellipsis">...</span>
                    )}
                    <motion.button
                      className={`table-pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {page}
                    </motion.button>
                  </React.Fragment>
                ))}

              <motion.button
                className="table-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title={t('table.next', 'Next')}
                whileHover={{ scale: currentPage === totalPages ? 1 : 1.05 }}
                whileTap={{ scale: currentPage === totalPages ? 1 : 0.95 }}
              >
                {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </motion.button>
            </div>

          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{t('table.rowsPerPage', 'Rows per page')}:</span>
            <select
              className="table-pagination-select"
              value={localPageSize}
              onChange={(e) => {
                setLocalPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </motion.div>
        );
      })()}
    </motion.div>
  );
}
